import { useState, type ReactNode } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { Alert, Divider, LoadingBlock, Muted, Stack, Strong, Textarea } from '../../components/ui';
import {
  useCancelStayBookingMutation,
  useStayCancellationPreviewQuery,
  type IStayBookingFieldsFragment,
  type IStayQuoteFieldsFragment,
} from '../../services/stayService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { CANCELLATION_POLICY_LABELS } from '../StayHostPage/stayForm';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { formatMoney, quoteLines } from '../StayGuestPage/stayQuoteLines';
import { guestCountsLabel, refundRuleText, stayDateRange } from './stayBooking';

export const Page = styled.main`
  ${tw`w-full px-4 pt-6 pb-20 mx-auto sm:px-6`}
  max-width: 1120px;
`;

export const Title = styled.h1`
  ${tw`text-2xl font-semibold sm:text-3xl`}
  margin: 0 0 4px;
  font-family: var(--font-display);
  color: var(--color-text-primary);
`;

export const Columns = styled.div`
  ${tw`grid gap-12 mt-6`}
  grid-template-columns: minmax(0, 1fr) 380px;
  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

export const Section = styled.section`
  ${tw`py-6`}
  border-bottom: 1px solid var(--color-border);
  &:last-child {
    border-bottom: none;
  }
`;

export const SectionHeading = styled.h2`
  ${tw`text-lg font-semibold`}
  margin: 0 0 12px;
  color: var(--color-text-primary);
`;

export const SideCard = styled.aside`
  ${tw`p-6`}
  position: sticky;
  top: 24px;
  align-self: start;
  border-radius: var(--radius-card, 12px);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-card);
  background: var(--color-bg-elevated, #fff);
  @media (max-width: 1023px) {
    position: static;
  }
`;

const LineRow = styled.div<{ $discount?: boolean }>`
  ${tw`flex justify-between gap-4 text-sm`}
  color: ${({ $discount }) => ($discount ? '#008a05' : 'var(--color-text-primary)')};
`;

const Thumb = styled.img`
  ${tw`object-cover`}
  width: 112px;
  height: 96px;
  border-radius: 10px;
  flex-shrink: 0;
  background: var(--color-bg-secondary);
`;

const DetailLabel = styled.div`
  ${tw`font-medium`}
  color: var(--color-text-primary);
`;

const DetailValue = styled.div`
  ${tw`text-sm mt-0.5`}
  color: var(--color-text-secondary);
`;

/** A label over its value ("Check-in" / "Sat, Mar 20, 3:00 PM"). */
export function Detail({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <DetailLabel>{label}</DetailLabel>
      <DetailValue>{children}</DetailValue>
    </div>
  );
}

const SummaryRow = styled.div`
  ${tw`flex gap-4 items-center`}
`;

const amount = (n: number) => (n < 0 ? `-${formatMoney(-n)}` : formatMoney(n));

/** The stay's price lines and total (spec 016 W20): the same lines at checkout and after. */
export function PriceBreakdown({
  quote,
  currency = 'USD',
  totalLabel = `Total (${currency})`,
  footer,
}: {
  quote: IStayQuoteFieldsFragment;
  /** Spec 028: the stay's currency (the brand's before booking, the receipt's after). */
  currency?: string;
  totalLabel?: string;
  footer?: ReactNode;
}) {
  return (
    <Stack $gap={4} data-testid="stay-price-breakdown">
      {quoteLines(quote).map(line => (
        <LineRow key={line.label} $discount={line.discount}>
          <span>{line.label}</span>
          <span>{amount(line.amount)}</span>
        </LineRow>
      ))}
      <Divider />
      <LineRow>
        <Strong>{totalLabel}</Strong>
        <Strong data-testid="stay-price-total">{formatMoney(quote.totalPrice)}</Strong>
      </LineRow>
      {footer}
    </Stack>
  );
}

/** Photo, name, dates and guests at the top of the side card. */
export function StayBookingSummary({
  name,
  imageUrl,
  subtitle,
}: {
  name?: string | null;
  imageUrl?: string | null;
  subtitle?: ReactNode;
}) {
  return (
    <SummaryRow>
      {imageUrl ? <Thumb src={toPublicS3ObjectUrl(imageUrl)} alt="" /> : <Thumb as="div" />}
      <Detail label={name || 'Place to stay'}>{subtitle}</Detail>
    </SummaryRow>
  );
}

export function bookingSubtitle(booking: IStayBookingFieldsFragment): string {
  const zone = booking.car?.location?.timezone;
  return [
    stayDateRange(booking.startAt, booking.endAt, zone),
    guestCountsLabel(booking.guestCounts),
  ]
    .filter(Boolean)
    .join(' · ');
}

export function PolicyText({
  policy,
}: {
  policy?: IStayBookingFieldsFragment['cancellationPolicySnapshot'];
}) {
  if (!policy) return <Muted>No cancellation policy was recorded for this booking.</Muted>;
  const label = CANCELLATION_POLICY_LABELS[policy];
  return (
    <p style={{ margin: 0 }}>
      <Strong>{label.title}.</Strong> {label.description}
    </p>
  );
}

/**
 * Refund preview, then cancellation (spec 016 §4.6, story 4). The preview comes from
 * the server, from the policy the booking was made with.
 */
export function CancelStayModal({
  bookingId,
  isOpen,
  onClose,
  onCancelled,
}: {
  bookingId: string;
  isOpen: boolean;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [reason, setReason] = useState('');
  const { data, loading, error } = useStayCancellationPreviewQuery({
    variables: { bookingId },
    skip: !isOpen,
    fetchPolicy: 'network-only',
  });
  const [cancel, { loading: cancelling }] = useCancelStayBookingMutation();
  const preview = data?.stayCancellationPreview;

  const confirm = async () => {
    try {
      await cancel({ variables: { bookingId, reason: reason.trim() || null } });
      toast.success('The stay was cancelled');
      onCancelled();
    } catch (e) {
      toast.error(stayErrorMessage(e, 'The stay could not be cancelled'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cancel stay" modalWidth="480px">
      <Stack $gap={4} data-testid="stay-cancel-modal">
        <Strong style={{ fontSize: 20 }}>Cancel this stay?</Strong>
        {loading && !preview ? <LoadingBlock label="Working out your refund" /> : null}
        {error ? (
          <Alert tone="error" title="Cannot cancel">
            {stayErrorMessage(error)}
          </Alert>
        ) : null}
        {preview ? (
          <>
            <Muted>{refundRuleText(preview.rule)}</Muted>
            <Stack $gap={4}>
              <LineRow>
                <span>Paid</span>
                <span>{formatMoney(preview.totalPaid)}</span>
              </LineRow>
              {preview.lodgingRefund > 0 && (
                <LineRow>
                  <span>Nights</span>
                  <span>{formatMoney(preview.lodgingRefund)}</span>
                </LineRow>
              )}
              {preview.cleaningFeeRefund > 0 && (
                <LineRow>
                  <span>Cleaning fee</span>
                  <span>{formatMoney(preview.cleaningFeeRefund)}</span>
                </LineRow>
              )}
              {preview.petFeeRefund > 0 && (
                <LineRow>
                  <span>Pet fee</span>
                  <span>{formatMoney(preview.petFeeRefund)}</span>
                </LineRow>
              )}
              {preview.guestServiceFeeRefund > 0 && (
                <LineRow>
                  <span>Service fee</span>
                  <span>{formatMoney(preview.guestServiceFeeRefund)}</span>
                </LineRow>
              )}
              {preview.occupancyTaxRefund > 0 && (
                <LineRow>
                  <span>Taxes</span>
                  <span>{formatMoney(preview.occupancyTaxRefund)}</span>
                </LineRow>
              )}
              <Divider />
              <LineRow>
                <Strong>{preview.cancelledBy === 'host' ? 'Guest refund' : 'Your refund'}</Strong>
                <Strong data-testid="stay-refund-amount">
                  {formatMoney(preview.refundAmount)}
                </Strong>
              </LineRow>
              {preview.nonRefundableAmount > 0 && (
                <LineRow>
                  <Muted $size="sm">Not refunded</Muted>
                  <Muted $size="sm">{formatMoney(preview.nonRefundableAmount)}</Muted>
                </LineRow>
              )}
            </Stack>
            {preview.hostPenalty ? (
              <Alert tone="warning" title="Host cancellation fee">
                {formatMoney(preview.hostPenalty)} comes out of a later payout, and these dates stay
                blocked on your calendar.
              </Alert>
            ) : null}
            <Textarea
              rows={3}
              maxLength={500}
              placeholder="Reason (optional)"
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
            <Button
              theme="danger"
              text="Cancel stay"
              fullWidth
              isLoading={cancelling}
              onClick={confirm}
              dataTestId="stay-cancel-confirm"
            />
          </>
        ) : null}
        <Button theme="text" text="Keep the stay" fullWidth onClick={onClose} />
      </Stack>
    </Modal>
  );
}
